import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { trackPurchase } from "@/lib/analytics";
import { useLang } from "@/context/LanguageContext";

const C = {
  fr: { checking: "Vérification du paiement…", checkingText: "Merci de patienter quelques instants.",
    success: "Paiement confirmé !", successText: "Votre dossier est désormais actif. Téléversez vos documents pour démarrer.",
    uploadDocs: "Téléverser mes documents", followFile: "Suivre mon dossier",
    pending: "Paiement en cours de traitement", pendingText: "Le statut sera mis à jour sous peu. Vous recevrez une confirmation par email.",
    backDash: "Retour au tableau de bord", errorTitle: "Paiement non abouti",
    errorText: "Une erreur est survenue ou le paiement a été annulé.", retry: "Réessayer" },
  en: { checking: "Verifying payment…", checkingText: "Please wait a few moments.",
    success: "Payment confirmed!", successText: "Your file is now active. Upload your documents to get started.",
    uploadDocs: "Upload my documents", followFile: "Track my file",
    pending: "Payment being processed", pendingText: "The status will be updated shortly. You will receive a confirmation by email.",
    backDash: "Back to dashboard", errorTitle: "Payment failed",
    errorText: "An error occurred or the payment was cancelled.", retry: "Try again" },
};

export default function PaymentReturn() {
  const { lang } = useLang();
  const c = C[lang];
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  const poll = useCallback(async (attempt = 0) => {
    if (attempt >= 6) { setState("pending"); return; }
    try {
      const { data } = await api.get(`/bookings/status/${sessionId}`);
      if (data.payment_status === "paid") {
        trackPurchase({
          transaction_id: sessionId,
          value: data.amount_total ? data.amount_total / 100 : 0,
          currency: (data.currency || "eur").toUpperCase(),
        });
        setState("success");
        return;
      }
      if (data.status === "expired") { setState("error"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch {
      setState("error");
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    poll();
  }, [sessionId, poll]);

  return (
    <div data-testid="payment-return" className="flex items-center justify-center min-h-[60vh]">
      <div className="bg-white border border-border p-10 max-w-md w-full text-center">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="mx-auto text-gold animate-spin" />
            <h1 className="font-heading text-2xl text-navy mt-6">{c.checking}</h1>
            <p className="text-muted-foreground mt-2">{c.checkingText}</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 size={48} className="mx-auto text-green-600" />
            <h1 className="font-heading text-3xl text-navy mt-6">{c.success}</h1>
            <p className="text-muted-foreground mt-2">{c.successText}</p>
            <div className="flex flex-col gap-3 mt-8">
              <Link to="/dashboard/documents" data-testid="goto-documents" className="bg-gold hover:bg-gold-hover text-white px-6 py-3 font-medium transition-colors flex items-center justify-center gap-2">{c.uploadDocs} <ArrowRight size={16} /></Link>
              <Link to="/dashboard/suivi" className="text-navy hover:text-gold transition-colors text-sm">{c.followFile}</Link>
            </div>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 size={48} className="mx-auto text-amber-500" />
            <h1 className="font-heading text-2xl text-navy mt-6">{c.pending}</h1>
            <p className="text-muted-foreground mt-2">{c.pendingText}</p>
            <Link to="/dashboard" className="inline-block mt-6 text-gold font-medium hover:underline">{c.backDash}</Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={48} className="mx-auto text-red-600" />
            <h1 className="font-heading text-2xl text-navy mt-6">{c.errorTitle}</h1>
            <p className="text-muted-foreground mt-2">{c.errorText}</p>
            <Link to="/nos-packs" className="inline-block mt-6 bg-gold hover:bg-gold-hover text-white px-6 py-3 font-medium transition-colors">{c.retry}</Link>
          </>
        )}
      </div>
    </div>
  );
}
